import { z } from "zod/v4"

import {
  ContactLanguageValue,
  ContactPortalValue,
  ContactSalutationValues,
  ContactTypeValues,
} from "../contact"

const phoneRegex = new RegExp("^[+]?[0-9() \\/.\\-]{1,21}[0-9]$")
const emailRegex = new RegExp(
  "^(?!\\.)(?!.*\\.\\.)([A-Z0-9_+-\\.]*)[A-Z0-9_+-]@([A-Z0-9][A-Z0-9\\-]*\\.)+[A-Z]{2,}$",
  "i"
)

const nameRegex = new RegExp("^([^0-9]*)$")

const getContactRequestValidationSchema = (isGermanBrand: boolean) => {
  return z.object({
    message: z
      .string({ error: "contactAgency_sendMessageErrorFieldMessageMissing" })
      .min(1, {
        error: "contactAgency_sendMessageErrorFieldMessageMissing",
      })
      .max(500, {
        error: "contactAgency_sendMessageErrorFieldTooLongMessage",
      }),
    personContact: z.object({
      email: z
        .string({ error: "contactAgency_sendMessageErrorFieldEmailMissing" })
        // error will not be displayed if the field has been filled but then emptied again
        .min(1, {
          error: "contactAgency_sendMessageErrorFieldEmailMissing",
        })
        .regex(emailRegex, {
          error: "contactAgency_sendMessageErrorFieldWrongEmail",
        }),
      salutation: z.enum(ContactSalutationValues, {
        error: "contactAgency_sendMessageErrorFieldSalutationMissing",
      }),
      lastName: z
        .string({ error: "contactAgency_sendMessageErrorFieldLastNameMissing" })
        .min(1, {
          error: "contactAgency_sendMessageErrorFieldLastNameMissing",
        })
        .regex(nameRegex, {
          error: "contactAgency_sendMessageErrorFieldFormatLastName",
        }),
      firstName: z
        .string({ error: "contactAgency_sendMessageErrorFieldFirstNameMissing" })
        .min(1, {
          error: "contactAgency_sendMessageErrorFieldFirstNameMissing",
        })
        .regex(nameRegex, {
          error: "contactAgency_sendMessageErrorFieldWrongName",
        }),
      phone: isGermanBrand
        ? z
            .string({ error: "contactAgency_sendMessageErrorFieldPhoneMissing" })
            .regex(phoneRegex, {
              error: "contactAgency_sendMessageErrorFieldWrongNumber",
            })
            .or(z.literal(""))
            .optional()
        : z.string({ error: "contactAgency_sendMessageErrorFieldPhoneMissing" }).regex(phoneRegex, {
            error: "contactAgency_sendMessageErrorFieldWrongNumber",
          }),
      language: z.enum(ContactLanguageValue).optional(),
    }),
  })
}

export const getContactFormValidationSchema = (isGermanBrand: boolean) => {
  return z.object({
    contactRequest: getContactRequestValidationSchema(isGermanBrand),
  })
}

export const getBffContactValidationSchema = (isGermanBrand: boolean) => {
  return z.object({
    body: z.object({
      portal: z.enum(ContactPortalValue),
      partnerId: z.string(),
      project: z.enum(ContactTypeValues),
      contactRequest: getContactRequestValidationSchema(isGermanBrand),
    }),
  })
}

export type Form = z.infer<ReturnType<typeof getContactFormValidationSchema>>
export type FormKey =
  | keyof Form["contactRequest"]["personContact"]
  | Exclude<keyof Form["contactRequest"], "personContact">
/**
 * In order of appearance in the form
 */
export const formKeyValues: readonly FormKey[] = [
  "salutation",
  "firstName",
  "lastName",
  "phone",
  "email",
  "message",
] as const
